import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Link, useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { OAuthButtons } from "@/components/OAuthButtons";
import { readPendingCartAdd } from "@/hooks/useCart";
import { ArrowLeft } from "lucide-react";

const loginSchema = z.object({
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export default function Login() {
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });
  const [, setLocation] = useLocation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Set when a logged-out tap on "Add to cart" sent them here; explains why
  // they're looking at a sign-in screen instead of the item they tapped.
  const [hasPendingCartAdd] = useState(() => readPendingCartAdd() != null);

  const onSubmit = async (values: LoginFormValues) => {
    setSubmitError(null);
    try {
      const response = await apiRequest("POST", "/api/login", values);
      const user = await response.json();
      queryClient.setQueryData(["/api/auth/user"], user);
      setLocation("/onboarding");
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Unable to log in. Please try again.",
      );
    }
  };

  return (
    <div className="min-h-screen bg-muted/20 py-10 px-4 flex items-center justify-center">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="-ml-2 mb-2 h-8 w-fit gap-1 px-2 text-muted-foreground"
            onClick={() => setLocation("/")}
            data-testid="button-back-to-landing"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <CardTitle>Welcome back</CardTitle>
          <CardDescription>
            {hasPendingCartAdd
              ? "Sign in or create an account to add that to your cart. We saved it for you."
              : "Sign in to access your neighborhood market."}
          </CardDescription>
        </CardHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <CardContent className="space-y-4">
              <OAuthButtons label="Sign in" />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="you@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="••••••••" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {submitError && <p className="text-sm text-destructive">{submitError}</p>}
              <p className="text-right text-sm">
                <Link href="/forgot-password" className="text-primary hover:underline">
                  Forgot your password?
                </Link>
              </p>
            </CardContent>
            <CardFooter className="flex flex-col gap-3 items-stretch">
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? "Signing in…" : "Sign In"}
              </Button>
              <p className="text-sm text-muted-foreground text-center">
                Need an account?{" "}
                <Link href="/register" className="text-primary hover:underline">
                  Register now
                </Link>
              </p>
              <p className="text-xs text-muted-foreground text-center">
                <a href="/terms-of-use" target="_blank" rel="noopener noreferrer" className="underline">
                  Terms of Use
                </a>{" "}
                &middot;{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline">
                  Privacy Policy
                </a>
              </p>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </div>
  );
}
